import { Button, Dialog, DialogContent, Text } from '@flatfile/baseline'
import { WistiaPlayer } from '@wistia/wistia-player-react'
import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { mixpanel } from '../support/mixpanel'

export const AutobuildWelcomeModal: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [isVisible, setIsVisible] = useState(false)

  // Check for welcome parameter and show modal
  useEffect(() => {
    const shouldShowWelcome = searchParams.get('welcome') === 'true'

    if (shouldShowWelcome) {
      // Track welcome modal shown
      mixpanel.track('Buildmode Onboarding Welcome Modal Shown')

      setIsVisible(true)

      // Clean up URL parameter
      const newSearchParams = new URLSearchParams(searchParams)
      newSearchParams.delete('welcome')
      setSearchParams(newSearchParams, { replace: true })
    }
  }, [searchParams, setSearchParams])

  const handleClose = () => {
    setIsVisible(false)
  }

  const handleLetsGo = () => {
    setIsVisible(false)
  }

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  return (
    <>
      {/* Custom blur overlay */}
      {isVisible && <div className='fixed inset-0 bg-black/30 backdrop-blur-sm z-[250]' onClick={handleClose} />}

      <Dialog open={isVisible} onOpenChange={setIsVisible}>
        <DialogContent className='max-w-2xl w-full p-0 bg-base-white border-none z-[300]'>
          {/* Video Section with Close Button */}
          <div className='w-full relative overflow-hidden rounded-t-lg'>
            <div
              className='aspect-video w-full bg-black-opacity-1 bg-cover bg-center'
              style={{
                backgroundImage: `url('https://fast.wistia.com/embed/medias/irx7lefj3g/swatch')`,
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            >
              <WistiaPlayer mediaId='irx7lefj3g' />
            </div>

            {/* Close Button */}
            <div className='absolute top-4 right-4'>
              <Button
                onClick={handleClose}
                variant='ghost'
                size='icon-md'
                icon='cross-large'
                iconProps={{
                  className: 'text-base-white',
                }}
                className='bg-black/50 hover:bg-black/70 rounded-full z-[301]'
              />
            </div>
          </div>

          {/* Content Section */}
          <div className='py-8 px-24'>
            {/* Header */}
            <div className='text-center mb-6'>
              <Text variant='title2' emphasized className='text-gray-900 mb-4 block'>
                Welcome to Autobuild
              </Text>

              <Text variant='body' className='text-text-default whitespace-pre-line block'>
                Just upload a sample data file or template, and Flatfile will instantly generate a custom workbook for
                you.
              </Text>
            </div>

            {/* Let's Go Button */}
            <div className='flex justify-center'>
              <Button onClick={handleLetsGo} variant='primary' size='lg' className='theme-static px-8'>
                Let's go
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
